'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Swiper as SwiperType } from 'swiper';
import { bannerAPI } from '@/lib/api';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// 辅助函数：将相对路径转换为完整的图片URL
function getImageUrl(imagePath: string | undefined | null): string {
  if (!imagePath) return '';
  
  // 如果已经是完整的URL，直接返回
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // 获取后端服务器地址（去掉/api后缀）
  const apiBaseUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api';
  const baseUrl = apiBaseUrl.replace('/api', '');
  
  // 确保路径以/开头
  const path = imagePath.startsWith('/') ? imagePath : '/' + imagePath;
  
  return baseUrl + path;
}

interface Slide {
  id: string;
  image: string;
  title?: string;
  subtitle?: string;
  link?: string;
}

interface HeroSliderProps {
  slides?: Slide[];
}

const defaultSlides: Slide[] = [
  {
    id: '1',
    image: '/hero-slide-1.jpg',
    title: 'Welcome to Our Company',
    subtitle: 'Professional products and services, trusted partner',
  },
  {
    id: '2',
    image: '/hero-slide-2.jpg',
    title: 'Innovation Technology, Leading the Future',
    subtitle: 'Advanced technology, excellent quality',
  },
];

export default function HeroSlider({ slides: propSlides }: HeroSliderProps) {
  // 从API获取轮播图数据
  const { data: bannersData, isLoading, error } = useQuery({
    queryKey: ['banners', 'active'],
    queryFn: () => bannerAPI.getList('active'),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // 调试日志
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Banners data:', {
        isLoading,
        error,
        bannersData,
        bannersCount: bannersData?.data?.length || 0,
      });
    }
  }, [isLoading, error, bannersData]);

  // 转换API数据为Slide格式
  const apiSlides: Slide[] = bannersData?.data?.map((banner) => {
    const imageUrl = getImageUrl(banner.imageUrl);
    // 调试日志
    if (process.env.NODE_ENV === 'development') {
      console.log('Banner image URL:', {
        original: banner.imageUrl,
        processed: imageUrl,
      });
    }
    return {
      id: banner.id,
      image: imageUrl,
      title: banner.title,
      subtitle: banner.subtitle,
      link: banner.link,
    };
  }) || [];

  // 优先使用API数据，如果没有则使用props或默认数据
  const slides = apiSlides.length > 0 ? apiSlides : (propSlides || defaultSlides);
  const swiperRef = useRef<SwiperType | null>(null);
  const prevButtonRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (swiperRef.current && prevButtonRef.current && nextButtonRef.current) {
      // Swiper会自动处理导航按钮
    }
  }, []);

  return (
    <section className="hero-slider w-full relative">
      <Swiper
        modules={[Autoplay, Navigation, Pagination]}
        spaceBetween={0}
        slidesPerView={1}
        autoplay={{
          delay: 5000,
          disableOnInteraction: false,
          pauseOnHover: true,
        }}
        loop={true}
        navigation={{
          prevEl: prevButtonRef.current,
          nextEl: nextButtonRef.current,
        }}
        pagination={{
          clickable: true,
          el: paginationRef.current,
        }}
        onSwiper={(swiper) => {
          swiperRef.current = swiper;
        }}
        className="hero-swiper"
      >
        {slides.map((slide) => {
          // slide.image 已经是处理过的完整URL（在apiSlides中已处理）
          const imageUrl = slide.image.startsWith('http') ? slide.image : getImageUrl(slide.image);
          if (!imageUrl) {
            console.warn('Missing image URL for slide:', slide);
            return null;
          }
          
          return (
            <SwiperSlide key={slide.id}>
              <div className="slide-container w-full h-full relative overflow-hidden">
                <img 
                  src={imageUrl} 
                  alt={slide.title || 'Banner'} 
                  className="slide-image w-screen max-w-none h-full object-cover"
                  style={{ width: '100vw', maxWidth: '100vw' }}
                  onError={(e) => {
                    console.error('Image failed to load:', {
                      url: imageUrl,
                      originalUrl: slide.image,
                      slide: slide,
                    });
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                  onLoad={() => {
                    if (process.env.NODE_ENV === 'development') {
                      console.log('Image loaded successfully:', imageUrl);
                    }
                  }}
                />
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>

      {/* 自定义导航按钮 */}
      <div
        ref={prevButtonRef}
        className="swiper-button-prev-custom absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-all shadow-lg"
      >
        <svg
          className="w-6 h-6 text-text-dark"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </div>
      <div
        ref={nextButtonRef}
        className="swiper-button-next-custom absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/80 hover:bg-white rounded-full flex items-center justify-center cursor-pointer transition-all shadow-lg"
      >
        <svg
          className="w-6 h-6 text-text-dark"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </div>

      {/* 自定义分页器 */}
      <div
        ref={paginationRef}
        className="swiper-pagination-custom absolute bottom-4 left-1/2 -translate-x-1/2 z-20"
      />
    </section>
  );
}

